import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import type { DailyCheckIn } from "../types";

const PAGE_SIZE = 28;

export const useCheckIns = (userId: string | undefined) => {
	const [checkIns, setCheckIns] = useState<DailyCheckIn[]>([]);
	const [loading, setLoading] = useState(true);
	const [page, setPage] = useState<number>(0);
	const [totalCount, setTotalCount] = useState<number>(0);

	const fetchCheckInsPage = useCallback(
		async (pageToFetch: number) => {
			setLoading(true);
			try {
				if (!userId) {
					console.error("No authenticated user - cannot fetch check-ins");
					setCheckIns([]);
					setTotalCount(0);
					return;
				}

				const from = pageToFetch * PAGE_SIZE;
				const to = from + PAGE_SIZE - 1;

				const { data, error, count } = await supabase
					.from("daily_checkins")
					.select("*, reflection:reflections(id, title, description)", {
						count: "exact",
					})
					.eq("user_id", userId)
					.order("created_at", { ascending: false })
					.range(from, to);

				if (error) throw error;

				const rawCheckIns = (data || []) as DailyCheckIn[];

				// Fetch missing reflections if any
				const missingReflectionIds = Array.from(
					new Set(
						rawCheckIns
							.filter((ci) => ci.reflection_id && !ci.reflection)
							.map((ci) => ci.reflection_id as string),
					),
				);

				if (missingReflectionIds.length > 0) {
					try {
						const { data: reflectionsData } = await supabase
							.from("reflections")
							.select("id, title, description")
							.in("id", missingReflectionIds);

						if (reflectionsData) {
							rawCheckIns.forEach((ci) => {
								if (ci.reflection_id && !ci.reflection) {
									const found = reflectionsData.find(
										(r: any) => r.id === ci.reflection_id,
									);
									if (found) ci.reflection = found as any;
								}
							});
						}
					} catch (err) {
						console.error("Failed to load missing reflections:", err);
					}
				}

				setCheckIns(rawCheckIns);
				setTotalCount(count || 0);
			} catch (error: any) {
				console.error("Error fetching paged check-ins:", error);
			} finally {
				setLoading(false);
			}
		},
		[userId],
	);

	useEffect(() => {
		if (userId) {
			fetchCheckInsPage(0);
		}
	}, [userId, fetchCheckInsPage]);

	useEffect(() => {
		if (userId) {
			fetchCheckInsPage(page);
		}
	}, [page, userId, fetchCheckInsPage]);

	// Adjust page if out of bounds
	useEffect(() => {
		const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
		if (page >= totalPages && totalPages > 0) {
			setPage(totalPages - 1);
		}
	}, [totalCount, page]);

	return {
		checkIns,
		loading,
		page,
		setPage,
		totalCount,
		pageSize: PAGE_SIZE,
		refetch: () => fetchCheckInsPage(page),
	};
};
